import { IApiResponse, IsApiResponse } from "@gemma/api-common";
import { GemmaError } from "@gemma/errors";
import { RecordAny } from "@gemma/typescript-types";
import { Arrays } from "@gemma/utils";
import { ApiResult, ApiResultFailure, IsApiErrorResult, IsApiResultSuccess } from "@timesheets/common";
import { IApiRequestModel } from "@timesheets/webapp-support";
import { TFormSubmitThis } from "./TFormSubmitThis";

export async function submit<
  //
  TFormData extends RecordAny,
  TApiRequestData,
  TResponseData,
  TFailureResponseData
>(
  //
  this: TFormSubmitThis<TFormData, TApiRequestData, TResponseData, TFailureResponseData>,
  event?: Event
): Promise<ApiResult<TResponseData, TFailureResponseData> | undefined> {
  let apiResult: ApiResult<TResponseData, TFailureResponseData> | undefined = undefined;
  let updatedForm: TFormData | undefined = undefined;
  let model: TApiRequestData | undefined = undefined;
  try {
    if (this.submitting) {
      //throttle?
      return undefined;
    }
    this.submitting = true;

    // if (this.validate) {
    this.errors = [];
    this.warnings = [];
    const uiValidator = (<{ validate?: (form: TFormData, error: GemmaError[]) => Promise<TFormData | undefined> }>this).validate;
    if (uiValidator) {
      const _form = await uiValidator(this.form, this.errors);
      if (_form) {
        updatedForm = _form;
      }
    }

    if (!updatedForm) {
      updatedForm = this.form;
    }

    if (this.submitApiRequest && this.submitApiRequest.getApiRequestModel) {
      const apiRequestModel = this.submitApiRequest.getApiRequestModel.call<
        //
        TFormSubmitThis<TFormData, TApiRequestData, TResponseData, TFailureResponseData>,
        [TFormData],
        IApiRequestModel<TFormData, TApiRequestData>
      >(this, updatedForm);

      const modelResult = await apiRequestModel.getModel();
      model = modelResult.model;
      Arrays.add(this.errors, modelResult.errors);

      this.validated = true;
    }

    if (this.errors.length === 0) {
      try {
        if (this.submitApiRequest) {
          apiResult = await this.submitApiRequest.makeApiRequest(this.$app.api, model as TApiRequestData, { form: updatedForm, eventBus: this.$app.constants.eventBus });
        } else {
          const apiResponse = await this.$app.api.submit<TApiRequestData, TResponseData, TFailureResponseData>(updatedForm, event as Event);
          if (IsApiResponse(apiResponse)) {
            apiResult = {
              succeeded: false,
              data: apiResponse as IApiResponse<TFailureResponseData>,
            };
          } else {
            apiResult = {
              succeeded: true,
              data: apiResponse as TResponseData,
            };
          }
        }
      } catch (innerError) {
        const error = innerError as GemmaError;
        this.errors.push(error);
      }
    }

    this.requestSucceeded = false;
    if (apiResult) {
      if (IsApiErrorResult(apiResult)) {
        const errors = Array.isArray(apiResult) ? apiResult : [apiResult];
        for (const error of errors) {
          const lineaError = new GemmaError(error.message, {
            data: error.data,
            ref: error.ref,
            ui: true,
            sensitive: false,
          });
          this.errors.push(lineaError);
        }

        await _onFailure.call(this, updatedForm, model);

        return apiResult;
      } else if (IsApiResultSuccess(apiResult)) {
        this.requestSucceeded = true;
        const OnSuccess = <{ onSuccess?(_data?: TResponseData): Promise<void> }>this;
        if (typeof OnSuccess.onSuccess === "function") {
          await OnSuccess.onSuccess.call(this, apiResult.data);
        }

        const Onsuccess = <{ success?(_data?: TResponseData): Promise<void> }>this;
        if (typeof Onsuccess.success === "function") {
          await Onsuccess.success.call(this, apiResult.data);
        }

        return apiResult;
      } else {
        for (const error of apiResult.data.meta.errors) {
          if (error instanceof GemmaError) {
            this.errors.push(error);
          } else {
            const lineaError = new GemmaError(error.message, {
              data: error.data,
              ref: error.ref,
              ui: true,
              sensitive: false,
            });
            this.errors.push(lineaError);
          }
        }

        await _onFailure.call(this, updatedForm, model, apiResult);

        return apiResult;
      }
    } else {
      await _onFailure.call(this, updatedForm, model, apiResult);
      return undefined;
    }
  } catch (error) {
    this.requestSucceeded = false;
    if (error instanceof GemmaError) {
      error.ui = true;
      error.sensitive = true;
      error.system = true;
      this.errors.push(error as GemmaError);
    } else {
      const gemmaError = new GemmaError((error as Error).message, { ui: true, sensitive: true, system: true, innerError: error as Error });
      this.errors.push(gemmaError);
    }
    await _onFailure.call(this, updatedForm || this.form, model, apiResult);
  } finally {
    this.submitting = false;

    if (this.notify && apiResult) {
      if (IsApiResultSuccess(apiResult)) {
        if (this.notify.success) {
          const success = typeof this.notify.success === "function" ? this.notify.success.call(this, updatedForm as TFormData) : this.notify.success;
          if (success) {
            this.$notify("general", success, { type: "info" });
          }
        }

        const onClear = <{ clear?(): void }>this;
        if (onClear.clear) {
          onClear.clear.call(this);
        }
      } else {
        if (this.notify.failure && this.errors.filter(e => e.ui).length === 0) {
          const failure = typeof this.notify.failure === "function" ? this.notify.failure.call(this, this.form, this.errors, apiResult as ApiResultFailure<TFailureResponseData>) : this.notify.failure;
          if (failure) {
            this.$notify("general", failure || "There was an unexpected error", { type: "error" });
          }
        }
      }
    }
  }
}

async function _onFailure<
  //
  TThis extends {
    //
    errors: GemmaError[];
    failure?(form: TFormData, _errors: GemmaError[], data?: TApiRequestData, apiResult?: ApiResult<TResponseData, TFailureResponseData>): Promise<void>;
  },
  TFormData extends RecordAny,
  TApiRequestData,
  TResponseData,
  TFailureResponseData
>(
  //
  this: TThis,
  form: TFormData,
  model?: TApiRequestData,
  apiResult?: ApiResult<TResponseData, TFailureResponseData>
) {
  if (typeof this.failure === "function") {
    await this.failure.call(this, form, this.errors, model, apiResult);
  }
}
